<template>
  <b-card>
    <div
      v-if="loadingData"
      class="d-flex justify-content-center py-3"
    >
      <b-spinner
        type="grow"
        label="Loading"
      />
    </div>

    <validation-observer
      v-else
      ref="infoRules"
      v-slot="{ invalid }"
      tag="form"
      @submit.prevent
    >
      <b-row>
        <b-col md="6">
          <validation-provider
            #default="validationContext"
            name="Name"
            rules="required"
          >
            <b-form-group
              label="Name"
              label-for="importer-name"
            >
              <b-form-input
                id="importer-name"
                v-model="importerForm.name"
                :state="getValidationState(validationContext)"
                trim
                placeholder="Importer name"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>

        <b-col md="6">
          <b-form-group
            label="Commercial Registration"
            label-for="importer-commercial-registration"
          >
            <b-form-input
              id="importer-commercial-registration"
              v-model="importerForm.commercial_registration"
              trim
              placeholder="Commercial registration number"
            />
          </b-form-group>
        </b-col>

        <b-col md="12">
          <b-form-group
            label="Address"
            label-for="importer-address"
          >
            <b-form-textarea
              id="importer-address"
              v-model="importerForm.address"
              rows="3"
              placeholder="Address"
            />
          </b-form-group>
        </b-col>

        <b-col md="12">
          <b-form-group
            label="Commercial Registration File (CR)"
            label-for="importer-cr"
          >
            <b-form-file
              id="importer-cr"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              placeholder="Choose a file..."
              drop-placeholder="Drop file here..."
              @change="onCrFileChange"
            />
            <div
              v-if="existingCrLink"
              class="mt-1 d-flex align-items-center"
            >
              <a
                :href="existingCrLink"
                target="_blank"
                rel="noopener"
                class="d-inline-flex align-items-center text-dark mr-1"
              >
                <feather-icon
                  icon="FileIcon"
                  size="18"
                  class="mr-50"
                />
                {{ importerForm.cr_file_name || 'View current CR file' }}
              </a>
            </div>
            <small class="text-muted d-block mt-25">
              Leave empty to keep the current file.
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-if="!loader"
            variant="primary"
            class="mr-1"
            :disabled="invalid"
            @click="saveImporter"
          >
            Save Changes
          </b-button>
          <b-button
            v-else
            variant="primary"
            disabled
            class="mr-1"
          >
            <b-spinner
              small
              type="grow"
              class="mr-50"
            />
            Saving...
          </b-button>
          <b-button
            variant="outline-secondary"
            :disabled="loader"
            @click="$router.push({ name: 'importer' })"
          >
            Cancel
          </b-button>
        </b-col>
      </b-row>
    </validation-observer>
  </b-card>
</template>

<script>
import { ref } from '@vue/composition-api'
import formValidation from '@core/comp-functions/forms/form-validation'
import axios from 'axios'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  name: 'AddEditImporter',
  data() {
    return {
      required,
      loader: false,
      loadingData: false,
      crFile: null,
    }
  },
  setup() {
    const { getValidationState } = formValidation()
    const importerForm = ref({
      name: '',
      address: '',
      commercial_registration: '',
      cr_url: '',
      cr_download_url: '',
      cr_file_name: '',
    })

    return {
      getValidationState,
      importerForm,
    }
  },
  computed: {
    existingCrLink() {
      return this.importerForm.cr_download_url
        || this.importerForm.cr_url
        || ''
    },
  },
  mounted() {
    this.fetchImporter()
  },
  methods: {
    unwrapImporter(payload) {
      const data = payload?.data ?? payload
      if (!data) return {}
      if (data.importer && typeof data.importer === 'object') return data.importer
      if (Array.isArray(data)) return data[0] || {}
      return data
    },
    async fetchImporter() {
      if (!this.$route.params.id) return

      this.loadingData = true
      try {
        const res = await axios.get(`importers/${this.$route.params.id}`)
        const record = this.unwrapImporter(decryptData(res.data.payload))
        this.importerForm = {
          ...this.importerForm,
          ...record,
        }
      } catch (error) {
        this.showError(error, 'Failed to load importer')
        this.$router.push({ name: 'importer' })
      } finally {
        this.loadingData = false
      }
    },
    onCrFileChange(event) {
      this.crFile = event.target.files?.[0] || null
    },
    showError(error, fallback) {
      let message = fallback
      try {
        if (error?.response?.data?.payload) {
          const payload = decryptData(error.response.data.payload)
          const msg = payload?.message || payload
          if (typeof msg === 'string') message = msg
          else if (typeof msg === 'object') {
            const firstList = Object.values(msg).find(Array.isArray)
            const [firstMessage] = firstList || []
            if (firstMessage) message = firstMessage
          }
        }
      } catch {
        // keep fallback
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: 'BellIcon',
          variant: 'danger',
        },
      })
    },
    async saveImporter() {
      if (!this.$route.params.id) return

      this.loader = true
      const formData = new FormData()
      formData.append('_method', 'put')
      formData.append('name', this.importerForm.name || '')
      formData.append('address', this.importerForm.address || '')
      formData.append('commercial_registration', this.importerForm.commercial_registration || '')

      if (this.crFile) {
        formData.append('cr', this.crFile)
      }

      try {
        const res = await axios.post(`importers/${this.$route.params.id}`, formData)
        if (res.status === 200 || res.status === 201) {
          this.$toasted.show('Updated Successfully', {
            position: 'top-center',
            duration: 3000,
          })
          this.$router.push({ name: 'importer' })
        }
      } catch (error) {
        this.showError(error, 'Failed to update importer')
      } finally {
        this.loader = false
      }
    },
  },
}
</script>
